import { css } from '@emotion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import Paper from '@mui/material/Paper';

import useFillViewportHeight from 'hooks/useFillViewportHeight';

const DAG_MIN_HEIGHT = 200;
const DAG_BOTTOM_GAP = 24;
const DAG_KEYBOARD_STEP = 24;
// `.run-dag-panel` is sticky at this offset, so a panel taller than the viewport minus the offset
// keeps its resize grip below the fold — unreachable for the rest of the page load.
const DAG_STICKY_TOP = 16;

export function dagMaxHeight(viewportHeight: number): number {
  return Math.max(DAG_MIN_HEIGHT, Math.round(viewportHeight - DAG_STICKY_TOP - DAG_BOTTOM_GAP));
}

export function clampDagHeight(height: number, viewportHeight: number): number {
  return Math.min(dagMaxHeight(viewportHeight), Math.max(DAG_MIN_HEIGHT, Math.round(height)));
}

function useViewportHeight(): number {
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);

  useEffect(() => {
    const update = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return viewportHeight;
}

export default function RunDagPanel({ children }: { children: ReactNode }) {
  const paperRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const fillHeight = useFillViewportHeight(paperRef, DAG_BOTTOM_GAP);
  const viewportHeight = useViewportHeight();
  const [override, setOverride] = useState<number | null>(null);
  const rawHeight = override ?? fillHeight;
  const height = rawHeight != null ? clampDagHeight(rawHeight, viewportHeight) : undefined;

  const applyDrag = (clientY: number) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOverride(clampDagHeight(drag.startHeight + clientY - drag.startY, viewportHeight));
  };

  const nudge = (delta: number) => {
    const paper = paperRef.current;
    if (!paper) return;
    setOverride(clampDagHeight(paper.getBoundingClientRect().height + delta, viewportHeight));
  };

  return (
    <Paper
      ref={paperRef}
      elevation={2}
      className="run-dag-panel"
      style={height != null ? { height } : undefined}
      css={css`
        flex-grow: 1;
        display: flex;
        flex-direction: column;
      `}
    >
      {children}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize DAG height"
        aria-valuenow={height}
        aria-valuemin={DAG_MIN_HEIGHT}
        aria-valuemax={dagMaxHeight(viewportHeight)}
        tabIndex={0}
        title="Drag or use the arrow keys to resize DAG height. Double-click or press Enter to reset."
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            nudge(event.key === 'ArrowUp' ? -DAG_KEYBOARD_STEP : DAG_KEYBOARD_STEP);
          } else if (event.key === 'Enter') {
            event.preventDefault();
            setOverride(null);
          }
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          const paper = paperRef.current;
          if (!paper) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = {
            startY: event.clientY,
            startHeight: paper.getBoundingClientRect().height,
          };
        }}
        onPointerMove={(event) => applyDrag(event.clientY)}
        onPointerUp={(event) => {
          applyDrag(event.clientY);
          dragRef.current = null;
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onDoubleClick={() => setOverride(null)}
        css={css`
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 10px;
          cursor: ns-resize;
          touch-action: none;
          user-select: none;
          z-index: 2;

          &::after {
            content: '';
            position: absolute;
            left: 50%;
            top: 3px;
            width: 36px;
            height: 3px;
            margin-left: -18px;
            border-radius: 2px;
            background: #c4c4c4;
          }

          &:focus-visible {
            outline: none;

            &::after {
              width: 72px;
              margin-left: -36px;
              background: #1976d2;
            }
          }
        `}
      />
    </Paper>
  );
}
